import React, { useCallback, useEffect, useRef, useState } from "react";
import { useUtilityAdminTranslation } from "../../../../hooks/useUtilityAdminTranslation";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { EyeIcon, RetryIcon } from "../../../../assets/icons";
import { FailedToLoadData } from "../../../../common/components/failed-to-load-data/FailedToLoadData";
import Button from "../../../../common/components/xepp/button/Button";
import EmptyState from "../../../../common/components/xepp/empty-state/EmptyState";
import StatCard from "../../../../common/components/xepp/stat-card/StatCard";
import StatusBadge from "../../../../common/components/xepp/status-badge/StatusBadge";
import SearchInput from "../../../../common/components/xepp/search-input/SearchInput";
import DataTable, { ColumnType } from "../../../../common/components/xepp/table/DataTable";
import { TablePagination } from "../../../../common/components/xepp/table-pagination/TablePagination";
import { useCan } from "../../../../guards/useCan";
import { useConfigLayout } from "../../../../hooks/useConfigLayout";
import { UtilityPermissions } from "../../../../model/permissions/permissions";
import { RefundCase } from "../../../../model/refunds/refund";
import { ApplicationRoutes } from "../../../../routes";
import { useGetRefundSummaryQuery, useGetRefundsQuery, useLazyExportRefundsQuery } from "../../../../service/refund-service";
import { formatCurrency } from "../../../../utils";
import { getErrorMessage } from "../../../../utils/error-handler";
import { formatDateTime } from "../../../admin/bulk-purchase/utils";
import RetryRefundModal from "./components/RetryRefundModal";
import styles from "./refunds.module.scss";
import { attemptsLabel, channelKey, refundDetailsPath, refundStatusKey, refundStatusVariant, relativeTimeParts } from "./refunds.utils";
import { useRetryRefund } from "./useRetryRefund";

const PAGE_SIZE = 10;

type RefundRow = RefundCase & { id: string };

export const REFUNDS_BASE_PATH = `/${ApplicationRoutes.ADMIN_UTIL_BASE}/${ApplicationRoutes.BILLS_OPERATIONS}/${ApplicationRoutes.REFUNDS}`;

/** Summary cards, search, the refund table and its pagination. */
const RefundsList: React.FC = () => {
	const { t } = useUtilityAdminTranslation();
	const navigate = useNavigate();
	const { can } = useCan();
	const { updateLayout } = useConfigLayout();
	const [searchParams, setSearchParams] = useSearchParams();

	const [search, setSearch] = useState(searchParams.get("search") ?? "");
	const [page, setPage] = useState(Number(searchParams.get("page")) || 1);

	const { data: summary } = useGetRefundSummaryQuery();
	const { data, isLoading, isFetching, isError, refetch } = useGetRefundsQuery({
		Page: page,
		PageSize: PAGE_SIZE,
		...(search && { Search: search }),
	});
	const [exportRefunds] = useLazyExportRefundsQuery();
	const retry = useRetryRefund();

	const canRetry = can(UtilityPermissions.RetryRefund);

	useEffect(() => {
		const params: Record<string, string> = {};
		if (page > 1) params.page = String(page);
		if (search) params.search = search;
		setSearchParams(params, { replace: true });
	}, [page, search, setSearchParams]);

	// The header's Export button is wired through the layout service, which only ever sees the
	// handler registered last — the ref keeps that handler on the current search.
	const searchRef = useRef(search);
	useEffect(() => {
		searchRef.current = search;
	}, [search]);

	const handleExport = useCallback(() => {
		toast.info(t("refunds.list.exportPreparing"));
		exportRefunds({ ...(searchRef.current && { Search: searchRef.current }) })
			.unwrap()
			.then(blob => {
				const url = URL.createObjectURL(blob);
				const link = document.createElement("a");
				link.href = url;
				link.download = `refunds-${new Date().toISOString().slice(0, 10)}.csv`;
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				URL.revokeObjectURL(url);
			})
			.catch((err: any) => toast.error(getErrorMessage(err?.data)));
	}, [exportRefunds, t]);

	useEffect(() => {
		updateLayout({ exportHandler: handleExport });
		return () => {
			updateLayout({ exportHandler: null });
		};
	}, [handleExport, updateLayout]);

	const handleSearch = (value: string) => {
		setSearch(value);
		setPage(1);
	};

	const openDetails = (refund: RefundCase) => {
		navigate(refundDetailsPath(REFUNDS_BASE_PATH, refund.transactionId), { state: { refundsSearch: searchParams.toString() } });
	};

	const renderUpdated = (iso: string) => {
		const parts = relativeTimeParts(iso);
		return parts ? t(`refunds.relativeTime.${parts.unit}`, { count: parts.count }) : formatDateTime(iso);
	};

	const columns: Array<ColumnType<RefundRow>> = [
		{
			header: t("refunds.list.columns.paymentDate"),
			accessor: "paymentDate",
			render: refund => <span>{formatDateTime(refund.paymentDate)}</span>,
		},
		{
			header: t("refunds.list.columns.customer"),
			accessor: "customerName",
			render: refund => <span className={refund.customerName ? "" : styles.muted}>{refund.customerName ?? t("refunds.unknownCustomer")}</span>,
		},
		{
			header: t("refunds.list.columns.reference"),
			accessor: "reference",
			render: refund => (
				<span title={refund.reference} className={styles.ellipsisText}>
					{refund.reference}
				</span>
			),
		},
		{
			header: t("refunds.list.columns.amountPaid"),
			accessor: "amount.amount",
			render: refund => <span>{formatCurrency(refund.amount.amount, { currency: refund.amount.currency })}</span>,
		},
		{
			header: t("refunds.list.columns.channel"),
			accessor: "paymentChannel",
			render: refund => <span className={styles.channelBadge}>{t(`refunds.channel.${channelKey(refund.paymentChannel)}`)}</span>,
		},
		{
			header: t("refunds.list.columns.paymentStatus"),
			accessor: "status",
			render: refund => <StatusBadge variant={refundStatusVariant(refund.status)}>{t(`refunds.status.${refundStatusKey(refund.status)}`)}</StatusBadge>,
		},
		{
			header: t("refunds.list.columns.attempt"),
			accessor: "attemptsUsed",
			render: refund => <span>{attemptsLabel(refund.attemptsUsed, refund.maxAttempts)}</span>,
		},
		{
			header: t("refunds.list.columns.updated"),
			accessor: "updatedAt",
			render: refund => <span>{renderUpdated(refund.updatedAt)}</span>,
		},
		{
			header: "",
			accessor: "id",
			render: refund =>
				refund.canRetry && canRetry ? (
					<Button
						leftIcon={<RetryIcon className={styles.retryIcon} />}
						variant="primary"
						text={t("refunds.list.actions.retry")}
						onClick={() => retry.requestRetry(refund)}
					/>
				) : (
					<Button leftIcon={<EyeIcon className={styles.actionIcon} />} variant="text" text={t("refunds.list.actions.view")} onClick={() => openDetails(refund)} />
				),
		},
	];

	const rows: RefundRow[] = (data?.items ?? []).map(refund => ({ ...refund, id: refund.transactionId }));

	const headerActions = <SearchInput initialValue={search} onSearch={handleSearch} placeholder={t("refunds.list.searchPlaceholder")} />;

	return (
		<div className={styles.page}>
			<div className={styles.summaryCards}>
				<StatCard label={t("refunds.summary.total")} value={summary?.total ?? "–"} />
				<StatCard label={t("refunds.summary.refunded")} value={summary?.refunded ?? "–"} tone="success" />
				<StatCard label={t("refunds.summary.inProgress")} value={summary?.inProgress ?? "–"} tone="warning" />
				<StatCard label={t("refunds.summary.failed")} value={summary?.failed ?? "–"} tone="danger" />
			</div>

			{isError ? (
				<div className={styles.errorState}>
					<FailedToLoadData text={t("refunds.list.loadFailed")} />
					<Button text={t("refunds.details.tryAgain")} variant="outline" onClick={() => refetch()} />
				</div>
			) : (
				<>
					<DataTable<RefundRow>
						data={rows}
						loading={isLoading}
						isUpdating={isFetching}
						columns={columns}
						headerActions={headerActions}
						onRowClick={openDetails}
						emptyComponent={<EmptyState title={t("refunds.list.emptyTitle")} description={t("refunds.list.emptyDescription")} />}
					/>
					<TablePagination isLoading={isFetching} totalItems={data?.count ?? 0} itemsPerPage={PAGE_SIZE} currentPage={page} onPageChange={setPage} />
				</>
			)}

			<RetryRefundModal isOpen={retry.target !== null} isLoading={retry.isRetrying} onConfirm={retry.confirm} onCancel={retry.cancel} />
		</div>
	);
};

export default RefundsList;
